import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadComorbidityDetails,
    loadEmploymentStatusDetails,
    loadGpDetails,
    loadReligionDetails,
    loadSexualOrientationDetails,
    loadSmokingTypes
} from '../../../../store/directusService';
import {
    clearParticipantHealthInformation,
    loadParticipantHealthInformation,
    updateParticipantHealthInformation
} from '../../../../store/participantService';
import { getConfiguredItems, getObjFromId } from '../../../../utils/directusFunctions';
import { MAX_EMAIL, MAX_NAME, MAX_PHONE } from '../../../../utils/formValidation/commonConstants';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../../utils/formValidation/commonErrorConstants';
import {
    COMORBIDITY_ERROR,
    EMPLOYMENT_STATUS_DETAILS_ERROR,
    GP_ERROR,
    RELIGION_ERROR,
    SEXUAL_ORIENTATION_DETAILS_ERROR,
    SMOKING_TYPES_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { ADVISER, hasRole, MANAGER, QUALITY, SUPERUSER } from '../../../../utils/userRoles';
import Button from '../../../formElements/Button';
import DateSelect from '../../../formElements/DateSelect';
import MultiSelect from '../../../formElements/MultiSelect';
import RadioButtons from '../../../formElements/RadioButtons';
import SingleSelect from '../../../formElements/SingleSelect';
import TextAreaField from '../../../formElements/TextAreaField';
import TextInputField from '../../../formElements/TextInputField';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import { validate } from './validateHealthInformation';

import form from '../../../../commonStyles/formStyles.module.css';

export const DOCTORS_STATEMENT_MAX_LENGTH = 100;
const acceptedRoles = [ADVISER, MANAGER, QUALITY, SUPERUSER];
const editOnClosedRoles = [SUPERUSER, QUALITY];
const message = 'Participant health details have been updated';

const HealthInformation = ({ expectedGpDetailsLength = 2000 }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        participantId: '',
        doctorsStatement: '',
        employmentStatusId: '',
        gpId: '',
        hasComorbidities: 'no',
        comorbidityIds: [],
        pregnant: false,
        pregnancyDueDate: '',
        midwifeName: '',
        midwifePhone: '',
        midwifeEmail: '',
        religionId: '',
        sexualOrientationId: '',
        smokingTypeId: '',
        employed: false
    };
    const [newEntry, setNewEntry] = useState(initialState);

    const [arrayGps, setArrayGps] = useState([]);
    const [arrayReligions, setArrayReligions] = useState([]);
    const [arrayEmploymentStatuses, setArrayEmploymentStatuses] = useState([]);
    const [arrayComorbidities, setArrayComorbidities] = useState([]);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const gpDetails = useSelector((state) => state.entities.directusService.gpDetails);
    const comorbidityDetails = useSelector(
        (state) => state.entities.directusService.comorbidityDetails
    );
    const religionDetails = useSelector((state) => state.entities.directusService.religionDetails);
    const sexualOrientationDetails = useSelector(
        (state) => state.entities.directusService.sexualOrientationDetails
    );
    const smokingTypes = useSelector((state) => state.entities.directusService.smokingTypes);
    const employmentStatusDetails = useSelector(
        (state) => state.entities.directusService.employmentStatusDetails
    );
    const healthInformation = useSelector(
        (state) => state.entities.participantService.currentParticipantHealthInformation
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);

    // USE EFFECTS
    useEffect(() => {
        if (gpDetails?.length < 1) {
            dispatch(loadGpDetails());
            dispatch(loadGpDetails(2));
            dispatch(loadGpDetails(3));
        }
        comorbidityDetails?.length < 1 && dispatch(loadComorbidityDetails());
        religionDetails?.length < 1 && dispatch(loadReligionDetails());
        sexualOrientationDetails?.length < 1 && dispatch(loadSexualOrientationDetails());
        smokingTypes?.length < 1 && dispatch(loadSmokingTypes());
        employmentStatusDetails?.length < 1 && dispatch(loadEmploymentStatusDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!currentParticipant?.id) return;
        setNewEntry((prev) => ({ ...prev, participantId: currentParticipant.id }));
        dispatch(loadParticipantHealthInformation(currentParticipant.id));
        setNewEntry(initialState);
        const disableOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(editOnClosedRoles, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus);
    }, [currentParticipant]);

    useEffect(() => {
        setNewEntry(
            Object.keys(healthInformation)?.length > 0
                ? healthInformation
                : { ...initialState, participantId: currentParticipant.id }
        );
    }, [healthInformation]);

    useEffect(() => {
        if (gpDetails?.length > expectedGpDetailsLength) {
            setArrayGps(getConfiguredItems(gpDetails, currentParticipant?.contractId));
        }
    }, [gpDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (comorbidityDetails?.length) {
            setArrayComorbidities(
                getConfiguredItems(comorbidityDetails, currentParticipant?.contractId)
            );
        }
    }, [comorbidityDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (religionDetails?.length) {
            setArrayReligions(getConfiguredItems(religionDetails, currentParticipant?.contractId));
        }
    }, [religionDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (employmentStatusDetails?.length) {
            setArrayEmploymentStatuses(
                getConfiguredItems(employmentStatusDetails, currentParticipant?.contractId)
            );
        }
    }, [employmentStatusDetails, currentParticipant?.contractId]);

    useEffect(() => {
        !isSubmitting && setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        setIsUpdateButtonDisabled(false);
        setIsSubmitting(false);
    }, [successMessage, errorMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
        if (arrayGps?.length < 1) return GP_ERROR;
        if (arrayComorbidities?.length < 1) return COMORBIDITY_ERROR;
        if (arrayReligions?.length < 1) return RELIGION_ERROR;
        if (arrayEmploymentStatuses?.length < 1) return EMPLOYMENT_STATUS_DETAILS_ERROR;
        if (smokingTypes?.length < 1) return SMOKING_TYPES_ERROR;
        if (sexualOrientationDetails?.length < 1) return SEXUAL_ORIENTATION_DETAILS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const cleanUp = () => dispatch(clearParticipantHealthInformation());

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...newEntry,
            pregnancyDueDate: newEntry.pregnant ? newEntry.pregnancyDueDate : ''
        };
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(payload, healthInformation);
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;
        setIsSubmitting(true);
        dispatch(updateParticipantHealthInformation(payload, message));
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div className={form.formWrapper}>
            <form
                onSubmit={onSubmit}
                className={form.form}
                data-testid="form-start-health-information">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="gpId"
                            label="GP"
                            placeholder="Select a GP..."
                            disabled={isDisabled}
                            menuItems={arrayGps || []}
                            selectedId={newEntry.gpId || ''}
                            selected={getObjFromId(arrayGps, newEntry.gpId)}
                            onChange={(chosenId) => onUpdate('gpId', chosenId)}
                        />

                        <TextAreaField
                            id="doctorsStatement"
                            label="Doctor's Statement"
                            placeholder="Enter doctor's statement"
                            disabled={isDisabled}
                            maxLength={DOCTORS_STATEMENT_MAX_LENGTH}
                            rows={4}
                            value={newEntry.doctorsStatement || ''}
                            count={`${newEntry.doctorsStatement?.length || 0}/${DOCTORS_STATEMENT_MAX_LENGTH}`}
                            onChange={(e) => onUpdate('doctorsStatement', e.target.value)}
                        />

                        <RadioButtons
                            id="hasComorbidities"
                            disabled={isDisabled}
                            label="Co-Morbidities"
                            radioButtons={[
                                { id: 'no', label: 'No', onChangeVal: 'no' },
                                { id: 'yes', label: 'Yes', onChangeVal: 'yes' },
                                {
                                    id: 'prefer not to say',
                                    label: 'Prefer not to say',
                                    onChangeVal: 'prefer not to say'
                                }
                            ]}
                            value={newEntry.hasComorbidities}
                            onChange={(option) => onUpdate('hasComorbidities', option)}
                        />
                        {newEntry.hasComorbidities === 'yes' && (
                            <MultiSelect
                                id="comorbidityIds"
                                label="Comorbidity"
                                disabled={isDisabled}
                                placeholder="Select comorbidities..."
                                menuItems={arrayComorbidities}
                                preSelectedIds={newEntry.comorbidityIds}
                                preSelects={newEntry.comorbidityIds.map((el) =>
                                    getObjFromId(arrayComorbidities, el)
                                )}
                                onChange={(chosenIds) => onUpdate('comorbidityIds', chosenIds)}
                            />
                        )}

                        <SingleSelect
                            id="smokingTypeId"
                            label="Smoker"
                            placeholder="Select a smoking type..."
                            disabled={isDisabled}
                            menuItems={smokingTypes || []}
                            selectedId={newEntry.smokingTypeId || ''}
                            selected={getObjFromId(smokingTypes, newEntry.smokingTypeId)}
                            onChange={(chosenId) => onUpdate('smokingTypeId', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <RadioButtons
                            id="pregnant"
                            label="Pregnant"
                            disabled={isDisabled}
                            value={newEntry.pregnant || false}
                            onChange={(option) => onUpdate('pregnant', option)}
                        />
                        {newEntry.pregnant && (
                            <DateSelect
                                id="pregnancyDueDate"
                                label="Pregnancy Due Date"
                                disabled={isDisabled}
                                value={newEntry.pregnancyDueDate}
                                isDefault={false}
                                onDateChange={(date) => onUpdate('pregnancyDueDate', date)}
                            />
                        )}
                        <div hidden={!newEntry.pregnant}>
                            <TextInputField
                                id="midwifeName"
                                label="Midwife's Name"
                                placeholder="Enter midwife's name"
                                disabled={isDisabled}
                                maxLength={MAX_NAME}
                                value={newEntry.midwifeName || ''}
                                error={errors.midwifeName}
                                onChange={(e) => onUpdate('midwifeName', e.target.value)}
                            />
                            <TextInputField
                                id="midwifePhone"
                                label="Midwife's Phone"
                                placeholder="Enter midwife's phone"
                                disabled={isDisabled}
                                maxLength={MAX_PHONE}
                                value={newEntry.midwifePhone || ''}
                                error={errors.midwifePhone}
                                onChange={(e) => onUpdate('midwifePhone', e.target.value)}
                            />
                            <TextInputField
                                id="midwifeEmail"
                                label="Midwife's Email"
                                placeholder="Enter midwife's email"
                                disabled={isDisabled}
                                maxLength={MAX_EMAIL}
                                value={newEntry.midwifeEmail || ''}
                                error={errors.midwifeEmail}
                                onChange={(e) => onUpdate('midwifeEmail', e.target.value)}
                            />
                        </div>
                        <RadioButtons
                            id="employed"
                            label="Employed"
                            disabled={isDisabled}
                            value={newEntry.employed || false}
                            onChange={(option) => onUpdate('employed', option)}
                        />

                        <SingleSelect
                            id="religionId"
                            label="Religion"
                            placeholder="Select a religion..."
                            disabled={isDisabled}
                            menuItems={arrayReligions}
                            selectedId={newEntry.religionId}
                            selected={getObjFromId(religionDetails, newEntry.religionId)}
                            onChange={(chosenId) => onUpdate('religionId', chosenId)}
                        />
                        <SingleSelect
                            id="employmentStatusId"
                            label="Employment Status"
                            placeholder="Select an employment status..."
                            disabled={isDisabled}
                            menuItems={arrayEmploymentStatuses}
                            selectedId={newEntry.employmentStatusId}
                            selected={getObjFromId(
                                employmentStatusDetails,
                                newEntry.employmentStatusId
                            )}
                            onChange={(chosenId) => onUpdate('employmentStatusId', chosenId)}
                        />
                        <SingleSelect
                            id="sexualOrientationId"
                            label="Sexual Orientation"
                            placeholder="Select a sexual orientation..."
                            disabled={isDisabled}
                            menuItems={sexualOrientationDetails}
                            selectedId={newEntry.sexualOrientationId}
                            selected={getObjFromId(
                                sexualOrientationDetails,
                                newEntry.sexualOrientationId
                            )}
                            onChange={(chosenId) => onUpdate('sexualOrientationId', chosenId)}
                        />
                    </div>
                </div>

                <Button
                    id="healthInformationUpdateButton"
                    content="Update Health Information"
                    disabled={isDisabled || isUpdateButtonDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

HealthInformation.propTypes = {
    expectedGpDetailsLength: PropTypes.number
};

export default HealthInformation;
